import { AvailableSubscription } from '@interfaces/general';
import { SUBSCRIPTION_TYPES_MAP } from '@utils/constants';

// Tipo específico para UI de suscripciones - no mover a general.d.ts
export type SubscriptionForUI = AvailableSubscription & {
  featured: boolean; // Específico de UI: determina styling
  isCurrentPlan: boolean; // Específico de UI: determina botones/acciones
};

// Alias para compatibilidad y claridad en contexto de categorización
export type ExtendedSubscription = SubscriptionForUI;

export interface CategorizedPlans {
  monthly: SubscriptionForUI[];
  annual: SubscriptionForUI[];
  highVolume: SubscriptionForUI[];
}

export const categorizePlans = (subscriptions: SubscriptionForUI[]): CategorizedPlans => {
  // Filtrar planes públicos usando is_public (principal criterio de filtrado)
  const publicPlans = subscriptions.filter((subscription) => subscription.is_public);

  const monthly: SubscriptionForUI[] = [];
  const annual: SubscriptionForUI[] = [];
  const highVolume: SubscriptionForUI[] = [];

  publicPlans.forEach((plan) => {
    const planId = plan.id.toString();

    // Criterio para planes de alto volumen (prioridad máxima)
    if (planId.includes('_MIL')) {
      highVolume.push(plan);
    }
    // Criterio para planes anuales (sufijo _A)
    else if (planId.endsWith('_A')) {
      annual.push(plan);
    }
    // Criterio para planes mensuales básicos (sin sufijo)
    else if (
      [
        SUBSCRIPTION_TYPES_MAP.STARTER.key,
        SUBSCRIPTION_TYPES_MAP.PLUS.key,
        SUBSCRIPTION_TYPES_MAP.PROFESIONAL.key,
      ].includes(planId)
    ) {
      monthly.push(plan);
    }
    // Otros planes públicos que no encajan en las categorías principales
    // se pueden agregar a una categoría por defecto o ignorar
  });

  // Ordenar cada categoría por el campo 'orden'
  const stableSort = (arr: SubscriptionForUI[]) =>
    arr.sort((a, b) => a.orden - b.orden || (a.precio ?? 0) - (b.precio ?? 0));

  return {
    monthly: stableSort(monthly),
    annual: stableSort(annual),
    highVolume: stableSort(highVolume),
  };
};

export const getPlanCategoryLabel = (category: keyof CategorizedPlans): string => {
  const labels = {
    monthly: 'Planes Mensuales',
    annual: 'Planes Anuales',
    highVolume: 'Planes de Alto Volumen',
  };
  return labels[category];
};

export const getPlanCategoryDescription = (category: keyof CategorizedPlans): string => {
  const descriptions = {
    monthly: 'Perfecto para empezar y hacer crecer tu negocio con flexibilidad mensual',
    annual: 'Ahorra 15% con el compromiso anual y características avanzadas',
    highVolume: 'Soluciones empresariales para comercios con alta demanda',
  };
  return descriptions[category];
};
